/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { BrowserCacheLocation, Configuration, LogLevel } from "@azure/msal-browser";

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */

export const msalConfig: Configuration = {
    auth: {
        authority: "https://login.microsoftonline.com/" + (process.env.REACT_APP_AZUREAD_TENANTID || "c0425278-3853-4e88-bc5f-1180b2601ac6"),        
        clientId: process.env.REACT_APP_AZUREAD_CLIENTID || "716c8c8b-927d-4293-99e5-6d1d3058c0ff",
        redirectUri: "/",     
        postLogoutRedirectUri: "/"
    },
    cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage, //"sessionStorage", // This configures where your cache will be stored
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },   
    system: {
        loggerOptions: {
            logLevel: LogLevel.Error,
            loggerCallback: (level: any, message: any, containsPii: any) => {
                if (containsPii) {
                    return;
                }                
                switch (level) {
                    case LogLevel.Error:
                        return;
                    case LogLevel.Info:
                        return;
                    case LogLevel.Verbose:
                        return;
                    case LogLevel.Warning:
                        return;
                    default:
                        return;
                }
            },
            piiLoggingEnabled: false
        },
    }
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: ["User.Read"]
};

export const apiRequest = {
    scopes: [`api://${msalConfig.auth.clientId}/iON.Api`]
};

export const powerBiRequest = {
    scopes: [`https://analysis.windows.net/powerbi/api/.default`]
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
    graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
    graphPhotoEndpoint: "https://graph.microsoft.com/v1.0/me/photo/$value",
    graphRolesEndpoint: "https://graph.microsoft.com/v1.0/me/appRoleAssignments",
    graphAllGroupsEndpoint: "https://graph.microsoft.com/v1.0/groups?$top=999&$select=id,description,displayName,isAssignableToRole",
};